import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useLogoFont } from "@/hooks/useLogoFont";
import { useCustomPages } from "@/hooks/useCustomPages";
import AdminEditField from "@/components/feature/AdminEditField";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import AdminSaveBar from "@/components/feature/AdminSaveBar";
import PreviewModeBar from "@/components/feature/PreviewModeBar";
import { InsertBar } from "./components/shared";
import BlockRenderer, { BLOCK_TYPES } from "./components/BlockRenderer";
import SectionBlockRenderer from "./components/SectionBlockRenderer";

export default function CustomPage() {
  const { slug } = useParams<{ slug: string }>();
  const { isAdmin, isEditing } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const navigate = useNavigate();
  const {
    pages,
    updateBlock,
    deleteBlock,
    moveBlock,
    insertBlock,
    updatePageTitle,
    togglePageVisibility,
  } = useCustomPages();

  const page = pages.find((p) => p.slug === slug);

  const bg = isDark ? "bg-gray-950" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";

  if (!page) {
    return (
      <div className={`min-h-screen flex flex-col ${bg}`}>
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 pt-20">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-pink-50 mx-auto mb-4">
              <i className="ri-file-unknow-line text-pink-400 text-2xl"></i>
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${headingColor}`}>Page Not Found</h1>
            <p className={`text-sm mb-6 ${subColor}`}>
              The page <span className="font-mono text-pink-500">/{slug}</span> doesn&apos;t exist yet.
              {isAdmin && " Create it in Admin \u2192 Make a Page."}
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate("/")} className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors">
                Go Home
              </button>
              {isAdmin && (
                <button onClick={() => navigate("/admin")} className={`border font-semibold px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "border-gray-600 text-gray-300" : "border-gray-200 text-gray-600"}`}>
                  Admin Center
                </button>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!page.visible && !isAdmin) {
    return (
      <div className={`min-h-screen flex flex-col ${bg}`}>
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 pt-20">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
              <i className="ri-eye-off-line text-gray-400 text-2xl"></i>
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${headingColor}`}>Coming Soon</h1>
            <p className={`text-sm mb-6 ${subColor}`}>This page isn&apos;t available yet.</p>
            <button onClick={() => navigate("/")} className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors">
              Go Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const hasSectionBlocks = page.blocks.some((b) => b.type === "section");
  const pageDescription = `${page.title} \u2014 Makers Alley Co. A curated handmade retail space in Medford, Oregon.`;

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${bg}`}>
      <SEOHead
        title={`${page.title} \u2014 Makers Alley Co`}
        description={pageDescription}
        canonicalPath={`/${page.slug}`}
        ogType="website"
        ogTitle={`${page.title} \u2014 Makers Alley Co`}
        ogDescription={pageDescription}
        lastModified={page.updatedAt ? page.updatedAt.slice(0, 10) : "2026-05-10"}
        author="Makers Alley Co"
        noIndex={!page.visible}
      />
      <PreviewModeBar />
      <Navbar />

      {isEditing && !page.visible && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 text-center mt-14">
          <p className="text-yellow-700 text-sm font-semibold">
            <i className="ri-eye-off-line mr-1"></i>
            This page is hidden from visitors. Toggle visibility in Admin &rarr; Make a Page.
          </p>
        </div>
      )}

      {isEditing && (
        <div className="bg-pink-50 border-b border-pink-100 px-6 py-2 text-center mt-14">
          <p className="text-pink-600 text-xs font-semibold">
            <i className="ri-edit-line mr-1"></i>
            Admin mode — click the controls on any block to edit, reorder, or delete. Use the Insert buttons to add new blocks.
          </p>
        </div>
      )}

      <div className={`pt-24 pb-12 px-6 text-center ${isDark ? "bg-gray-900" : "bg-gradient-to-br from-pink-50 to-green-50"} ${isEditing ? "mt-0" : "mt-14"}`}>
        <div className="max-w-3xl mx-auto">
          {isEditing ? (
            <AdminEditField value={page.title} onSave={(v) => updatePageTitle(page.id, v)} inputClassName="text-4xl font-extrabold text-center">
              <h1 className={`text-4xl md:text-5xl font-extrabold ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {page.title}
              </h1>
            </AdminEditField>
          ) : (
            <h1 className={`text-4xl md:text-5xl font-extrabold ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
              {page.title}
            </h1>
          )}
        </div>
      </div>

      <main className={`flex-1 ${hasSectionBlocks ? "w-full" : "max-w-3xl mx-auto w-full px-6"} py-8`}>
        {page.blocks.length === 0 ? (
          <div className="text-center py-16 max-w-3xl mx-auto px-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
              <i className="ri-layout-line text-gray-400 text-xl"></i>
            </div>
            <p className={`text-sm font-semibold mb-1 ${headingColor}`}>No content yet</p>
            <p className={`text-xs ${subColor}`}>{isEditing ? "Use the insert bar below to add your first block." : "Check back soon."}</p>
            {isEditing && (
              <div className="mt-6">
                <InsertBar onInsert={(type) => insertBlock(page.id, -1, type)} isDark={isDark} />
              </div>
            )}
          </div>
        ) : (
          <>
            {isEditing && (
              <div className="max-w-3xl mx-auto px-6">
                <InsertBar onInsert={(type) => insertBlock(page.id, -1, type)} isDark={isDark} />
              </div>
            )}
            {page.blocks.map((block, idx) => (
              <div key={block.id} className={`relative ${block.type === "section" ? "w-full" : "max-w-3xl mx-auto px-6"}`}>
                {block.type === "section" ? (
                  <SectionBlockRenderer
                    block={block}
                    isAdmin={isEditing}
                    isDark={isDark}
                    headingFont={headingFont}
                    headingStyle={headingStyle}
                    onDelete={() => deleteBlock(page.id, idx)}
                    onMoveUp={() => moveBlock(page.id, idx, -1)}
                    onMoveDown={() => moveBlock(page.id, idx, 1)}
                    isFirst={idx === 0}
                    isLast={idx === page.blocks.length - 1}
                    onUpdate={(b) => updateBlock(page.id, idx, b)}
                  />
                ) : (
                  <BlockRenderer
                    block={block}
                    isAdmin={isEditing}
                    isDark={isDark}
                    headingFont={headingFont}
                    headingStyle={headingStyle}
                    onUpdate={(b) => updateBlock(page.id, idx, b)}
                    onDelete={() => deleteBlock(page.id, idx)}
                    onMoveUp={() => moveBlock(page.id, idx, -1)}
                    onMoveDown={() => moveBlock(page.id, idx, 1)}
                    isFirst={idx === 0}
                    isLast={idx === page.blocks.length - 1}
                  />
                )}
                {isEditing && (
                  <div className="max-w-3xl mx-auto px-6">
                    <InsertBar onInsert={(type) => insertBlock(page.id, idx, type)} isDark={isDark} />
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {isEditing && (
          <div className="mt-12 pt-8 border-t border-dashed border-gray-200 flex items-center justify-center gap-4 max-w-3xl mx-auto px-6">
            <button
              onClick={() => navigate("/admin")}
              className={`text-sm font-semibold cursor-pointer transition-colors flex items-center gap-1.5 ${isDark ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
            >
              <i className="ri-settings-3-line"></i>Edit in Admin Panel
            </button>
            <span className={`text-xs ${subColor}`}>&middot;</span>
            <button
              onClick={() => togglePageVisibility(page.id)}
              className={`text-sm font-semibold cursor-pointer transition-colors flex items-center gap-1.5 ${page.visible ? "text-green-500 hover:text-green-700" : "text-gray-400 hover:text-gray-700"}`}
            >
              <i className={page.visible ? "ri-eye-line" : "ri-eye-off-line"}></i>
              {page.visible ? "Page is Live" : "Page is Hidden"}
            </button>
          </div>
        )}
      </main>

      <Footer />
      {isAdmin && <AdminSaveBar />}
    </div>
  );
}